import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { JustificationFormDialog } from './JustificationFormDialog';
import { CATEGORY_LABEL_KEY, QUEUE_EMPTY_KEY, QUEUE_LABEL_KEY, STATUS_LABEL_KEY } from './justificationLabels';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { ExportButton } from '../shared/ExportButton';
import { StaffPicker } from '../shared/StaffPicker';
import { ApiError } from '../../lib/api/client';
import {
  justificationsApi,
  type JustificationQuery,
  type JustificationRow,
  type JustificationStatusFilter,
} from '../../lib/api/justifications';
import { formatTimestamp } from '../../lib/format/dateTime';
import { mapDetails } from '../reference/referenceFormHelpers';
import { canReadJustifications } from '../../lib/permissions/justifications';
import styles from './JustificationList.module.css';

/**
 * The justifications queue (M-11, Task 4, spec §3) — `/justifications`, a
 * nav entry that pointed nowhere before this milestone (spec §0: the
 * legacy's own supervisor list rejected the only role its own sidebar link
 * was shown to). Copies `EscalationList.tsx`'s own structure — the newest
 * reviewed screen, carrying the fixes M-09 paid for: a segmented control
 * over the server's own status filter, a draft/applied filter row, a
 * cursor-paginated table.
 *
 * ## `status` defaults to `all`, not `open` (spec §2's own warning, restated for the UI)
 *
 * Unlike `EscalationList.tsx`'s `open` starting segment, this screen starts
 * on `all`. A four-state lifecycle where two states (`approved`/`rejected`)
 * are terminal has no honest single "queue" the way `open` is for an
 * escalation — `JustificationIndexRequest`'s own class docblock states the
 * identical fact for the SERVER's default, and M-09 already shipped a panel
 * whose default queue excluded every row a caller had just created. Picking
 * `open` here on the UI side alone would reproduce that failure one layer up
 * even though the wire default is safe.
 *
 * ## "About me" and "Raised by me" both narrow server-side (raised-by-me fix)
 *
 * "About me" is `mine: true` — `subject_id = caller`. "Raised by me" is
 * `raised_by_me: true` — `raised_by = caller`. Both are real
 * `JustificationIndexRequest` filters, both serialised `1`/`0` (spec §2,
 * never the string `'true'`), and both independent (a caller may set
 * either, neither, or both — see `JustificationIndexRequest`'s own class
 * docblock for why combining them is AND, not OR). "Raised by me" ONCE had
 * no server-side equivalent and was applied to the page already returned
 * instead — a defect on a cursor-paginated list: a caller's own raised
 * complaint can land on any page, so narrowing only the fetched page could
 * tell a caller there were none when there were, just on a later page. The
 * raised-by-me fix replaced that with the real query parameter mirrored
 * here.
 *
 * ## The subject picker is a decider's own filter, never a security check
 *
 * `subject` (a user uuid) requires `justification.decide` server-side
 * (`JustificationIndexRequest`'s own docblock: it can only NARROW an
 * already-unrestricted result for a decider). Shown here only when
 * `canDecide`, as a `StaffPicker` in the FilterBar — it used to be a raw
 * uuid text box, because the user directory (`usersApi`) needs `user.view`,
 * which a decider does not necessarily hold. `GET /lookups/staff` is opened
 * by `justification.decide` (the API's `LookupPolicy`), so the picker never
 * 403s for the people who can see it. The wire value is unchanged: the
 * picked person's uuid.
 */

export interface JustificationListProps {
  permissions: readonly string[];
  currentUserUuid: string;
  onOpen: (uuid: string) => void;
  /**
   * Whether this list is the view on screen. The screen keeps the list
   * mounted (hidden) under an opened record, and `PageActions` portals into
   * the shell's title row whatever `hidden` says — so a hidden list withdraws
   * its page commands, or they would sit beside the detail.
   */
  active?: boolean;
}

interface JustificationListRow extends Row, JustificationRow {
  /** Flattened, plain-string duplicates of `subject.full_name`/`raiser.full_name` — `DataTable`'s own `leadingCell()` only turns the FIRST column into the clickable opener when that column carries no `render`, and a `render` is required for `subject`/`raiser` because each is an object, not a primitive `row[column.key]` could print directly. */
  subject_name: string;
  raiser_name: string;
}

function toRow(node: JustificationRow): JustificationListRow {
  return { ...node, id: node.uuid, subject_name: node.subject.full_name, raiser_name: node.raiser.full_name };
}

const QUEUES: readonly JustificationStatusFilter[] = ['open', 'answered', 'approved', 'rejected', 'all'];

interface FilterState {
  mine: boolean;
  raisedByMe: boolean;
  subject: string;
}

const BLANK_FILTERS: FilterState = { mine: false, raisedByMe: false, subject: '' };

function toQuery(
  status: JustificationStatusFilter,
  filters: FilterState,
  canDecide: boolean,
  cursor?: string,
  perPage?: number,
): JustificationQuery {
  const query: JustificationQuery = { status };
  if (filters.mine) query.mine = true;
  if (filters.raisedByMe) query.raised_by_me = true;
  if (canDecide && filters.subject.trim() !== '') query.subject = filters.subject.trim();
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

/** Read off the OUTGOING QUERY, not `appliedFilters` state — `EscalationList.tsx`'s own `hasAppliedFilters` shape. `status` is excluded: it always travels. `raisedByMe` now DOES reach the wire (the raised-by-me fix) — it counts here alongside `mine`/`subject`. */
function hasAppliedFilters(query: JustificationQuery): boolean {
  return query.mine !== undefined || query.raised_by_me !== undefined || query.subject !== undefined;
}

export function JustificationList({ permissions, onOpen, active = true }: JustificationListProps) {
  const { t, i18n } = useTranslation();
  const canCreate = permissions.includes('justification.create');
  const canDecide = permissions.includes('justification.decide');

  const [statusFilter, setStatusFilter] = useState<JustificationStatusFilter>('all');
  const [status, setStatus] = useState<ListStatus>('loading');
  const [fetchedRows, setFetchedRows] = useState<readonly JustificationListRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  /**
   * `JustificationIndexRequest::rules()` validates `subject` `uuid` +
   * `exists`. A picked uuid is real when picked, but the account can be
   * deleted between the pick and Apply — and before the picker this was free
   * text, where a 422 was the normal path. Discarding `error.details` would
   * leave only the generic "queue could not be loaded", with a Retry that
   * resends the same invalid filter forever, so the server's own keyed reason
   * is surfaced against the field itself.
   */
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `JustificationIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);
  const [formOpen, setFormOpen] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: JustificationQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);
    setSubjectError(null);

    try {
      const page = await justificationsApi.list(query);
      if (seq !== requestSeq.current) return;
      setFetchedRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setSubjectError(mapDetails(error.details).subject ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(statusFilter, appliedFilters, canDecide, pages.cursor ?? undefined, pages.perPage));
  }, [load, statusFilter, appliedFilters, canDecide, pages.cursor, pages.perPage]);

  function handleStatusChange(value: string): void {
    setStatusFilter(value as JustificationStatusFilter);
    pages.first();
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(statusFilter, appliedFilters, canDecide, pages.cursor ?? undefined, pages.perPage));
  }

  // A freshly filed complaint is ALWAYS `status: 'open'`
  // (`JustificationController::store()`) — prepending it unconditionally
  // would put an open row into whichever queue happened to be selected,
  // including "Approved" and "Rejected" (the identical M-1 lesson
  // `EscalationList.tsx`'s own `handleCreated` states). It belongs only in
  // the queues that would ever show an open row: "Open" itself and "All".
  function handleCreated(created: JustificationRow): void {
    if (statusFilter === 'open' || statusFilter === 'all') {
      setFetchedRows((current) => [toRow(created), ...current]);
      if (status === 'empty') setStatus('ready');
    }
    setFormOpen(false);
  }

  function renderCategory(row: Row): ReactNode {
    return t(CATEGORY_LABEL_KEY[(row as unknown as JustificationRow).category]);
  }

  function renderStatus(row: Row): ReactNode {
    return t(STATUS_LABEL_KEY[(row as unknown as JustificationRow).status]);
  }

  function renderOccurredAt(row: Row): ReactNode {
    const value = (row as unknown as JustificationRow).occurred_at;
    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  const queueOptions = QUEUES.map((value) => ({ value, label: t(QUEUE_LABEL_KEY[value]) }));

  const columns: readonly Column[] = [
    // NO `render` HERE, DELIBERATELY — `DataTable`'s own `leadingCell()` only
    // turns the FIRST column into the clickable row opener when it carries
    // no `render` (this file's own `JustificationListRow` docblock).
    { key: 'subject_name', label: t('justifications.fieldSubject') },
    { key: 'category', label: t('justifications.fieldCategory'), render: renderCategory },
    { key: 'occurred_at', label: t('justifications.fieldOccurredAt'), render: renderOccurredAt },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'raiser_name', label: t('justifications.fieldRaiser') },
  ];

  return (
    <div className={styles['page']}>
      {/* No second heading under the shell's `<h1>`: "New complaint" and
          Export sit in the title row. `canDecide` is passed to `toQuery`
          because it decides whether `subject` reaches the wire at all: a
          caller without `justification.decide` must not export by a filter
          their own list never applied. No cursor. */}
      {active ? (
        <PageActions>
          {canCreate ? (
            <Button
              onClick={() => {
                setFormOpen(true);
              }}
              icon={<PlusIcon size={16} />}
            >
              {t('justifications.newComplaint')}
            </Button>
          ) : null}
          <ExportButton
            module="justifications"
            filters={{ ...toQuery(statusFilter, appliedFilters, canDecide) }}
            canExport={canReadJustifications(permissions)}
          />
        </PageActions>
      ) : null}

      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        leading={
          <SegmentedControl
            label={t('justifications.queueLabel')}
            options={queueOptions}
            value={statusFilter}
            onChange={handleStatusChange}
          />
        }
      >
        <label className={styles['mineOption']}>
          <input
            type="checkbox"
            checked={draftFilters.mine}
            onChange={(event) => {
              const { checked } = event.target;
              setDraftFilters((current) => ({ ...current, mine: checked }));
            }}
          />
          {t('justifications.aboutMe')}
        </label>

        <label className={styles['mineOption']}>
          <input
            type="checkbox"
            checked={draftFilters.raisedByMe}
            onChange={(event) => {
              const { checked } = event.target;
              setDraftFilters((current) => ({ ...current, raisedByMe: checked }));
            }}
          />
          {t('justifications.raisedByMe')}
        </label>

        {canDecide ? (
          <StaffPicker
            label={t('justifications.subjectFilterLabel')}
            width="fill"
            className={styles['subjectFilter']}
            value={draftFilters.subject === '' ? null : draftFilters.subject}
            error={subjectError ?? undefined}
            onChange={(uuid) => {
              setDraftFilters((current) => ({ ...current, subject: uuid }));
              setSubjectError(null);
            }}
          />
        ) : null}
      </FilterBar>

      <DataTable
        caption={t('justifications.tableCaption')}
        columns={columns}
        rows={fetchedRows}
        status={status}
        empty={{ body: t(QUEUE_EMPTY_KEY[statusFilter]) }}
        noResults={{ title: t('list.noResultsTitle') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('justifications.queueLoadFailed') }}
        onRowActivate={(row) => {
          onOpen(row.id);
        }}
      />

      <Pagination
        pages={pages}
        total={total}
        rowsOnPage={fetchedRows.length}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="justificationListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <JustificationFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={handleCreated}
      />
    </div>
  );
}
